import React from "react";
import DynamicFieldConfigType from "../types/DynamicFieldConfigType";
import FormTextField from "../../fields/textField";
import DynamicFieldLabel from "./DynamicFieldLabel";
import { useDynamicFormRenderer } from "../context/dynamicFormRendererContext";
import { useValidateEmailDomain } from "../../../dataloader";
import { useFormikContext } from "formik";
import FieldAlias from "../enums/FieldAlias";
import { useCopies } from "../../../hooks/useCopies";
import get from "lodash/get";

/**
 * @name EmailFieldWrapper
 * @interface DynamicFieldConfigType
 * @param validationFn
 * @param props
 * @constructor
 */
export default function EmailFieldWrapper({ validationFn, ...props }: DynamicFieldConfigType) {
  const copies = useCopies();
  const { setFieldError, errors } = useFormikContext();
  const { validateEmailDomain, invalidEmailDomainErrorMessage, registrationFormId } = useDynamicFormRenderer();
  const [domainValidationResult, setDomainValidationResult] = React.useState<
    { email: string; valid: boolean } | undefined
  >();
  const { mutateAsync: validateEmailDomainApi } = useValidateEmailDomain(true);

  const shouldValidateEmailDomain = React.useMemo(() => {
    return props.schemaObj.alias === FieldAlias.WORK_EMAIL && validateEmailDomain && registrationFormId;
  }, [props.schemaObj.alias, validateEmailDomain, registrationFormId]);

  const onBlurHandler = React.useCallback(
    async e => {
      const workEmail = e?.target?.value?.trim();
      if (shouldValidateEmailDomain && workEmail && !get(errors, props.schemaObj.id)) {
        try {
          const isValid = await validateEmailDomainApi({ params: { registrationFormId, workEmail } });
          setDomainValidationResult({ email: workEmail, valid: isValid });
          if (!isValid) {
            setFieldError(props.schemaObj.id, invalidEmailDomainErrorMessage || copies.get("invalid.email"));
          }
        } catch (e) {}
      }
    },
    [
      setFieldError,
      props.schemaObj.id,
      shouldValidateEmailDomain,
      copies,
      invalidEmailDomainErrorMessage,
      registrationFormId,
      validateEmailDomainApi,
      errors
    ]
  );

  const validationWrapper = React.useCallback(
    async fieldValue => {
      const validationError = await validationFn?.(fieldValue);
      if (validationError) {
        return validationError;
      }
      if (
        shouldValidateEmailDomain &&
        domainValidationResult?.email === fieldValue &&
        domainValidationResult?.valid === false
      ) {
        return invalidEmailDomainErrorMessage || copies.get("invalid.email");
      }
    },
    [domainValidationResult, invalidEmailDomainErrorMessage, copies, validationFn, shouldValidateEmailDomain]
  );

  return (
    <React.Fragment>
      <FormTextField
        name={props.schemaObj.id}
        label={<DynamicFieldLabel schemaObj={props.schemaObj} />}
        disabled={props.schemaObj.disabled}
        required={props.schemaObj.mandatory}
        validate={validationWrapper}
        placeholder={props.schemaObj.placeholderLabel}
        conflictValue={props.schemaObj.conflictValue}
        fullWidth={props.schemaObj.fullWidthView}
        indented={props.schemaObj.indented}
        className={"transformToLowerCase"}
        onBlur={shouldValidateEmailDomain ? onBlurHandler : undefined}
      />
    </React.Fragment>
  );
}
